/**
 * ChatWindow — the core chat UI, shared between the floating widget popup
 * and the full-page /chat view.
 *
 * Props:
 *   messages        {Array}    list of {id, role, content, ts, confidence}
 *   onSend          {Function} (text) => void
 *   loading         {Boolean}  true while waiting for bot reply
 *   compact         {Boolean}  true for floating-popup (smaller, no sidebar)
 *   onOpenFullPage  {Function} optional — shows "Open full chat" button
 *   sessionId       {String}   current session id (for deep-link)
 *   hasUnread       {Boolean}  drives the unread badge
 *   isAdmin         {Boolean}  shows source (DB cache vs Claude) + API cost per bot message
 */

import React, { useRef, useEffect, useState } from "react";
import { Spinner } from "reactstrap";

// Runtime white-label config — set by nginx-mounted /app-config.js
const appName = (window.APP_CONFIG || {}).appName || "Fincrest Systems";

const BOT_AVATAR = "🤖";
const BOT_NAME   = `${appName} AI`;

const DISCLAIMER =
  `⚠️ ${BOT_NAME} provides platform guidance only. Nothing here constitutes ` +
  "financial advice or a recommendation to buy/sell securities. Always consult " +
  "a registered investment advisor (SEC/FINRA) before making trading decisions.";

/* ── admin-only source/cost meta line (mirrors AdminChatView) ────────── */
function MessageMeta({ msg }) {
  if (!msg.modelUsed) return null;

  if (msg.modelUsed === "cache") {
    return (
      <div style={{ fontSize: 10, color: "#aaa", marginTop: 3, paddingLeft: 4 }}>
        Local DB
        {msg.cacheEntryId != null
          ? <span style={{ color: "#667eea", marginLeft: 4 }}>· KB #{msg.cacheEntryId}</span>
          : null}
      </div>
    );
  }

  const modelLabel = msg.modelUsed.includes("haiku")  ? "Haiku"
                    : msg.modelUsed.includes("sonnet") ? "Sonnet"
                    : msg.modelUsed;

  return (
    <div style={{ fontSize: 10, color: "#aaa", marginTop: 3, paddingLeft: 4 }}>
      Claude API · {modelLabel}
      {msg.costUsd != null && msg.costUsd > 0
        ? <span style={{ color: "#e67e22", marginLeft: 4 }}>· ${msg.costUsd.toFixed(6)}</span>
        : null}
    </div>
  );
}

/* ── individual message bubble ─────────────────────────────────────── */
function MessageBubble({ msg, compact, isAdmin }) {
  const isUser = msg.role === "user";
  const isBot  = msg.role === "bot" || msg.role === "assistant";
  const isSystem = msg.role === "system";     // e.g. "Admin has replied"
  const isEscalated = msg.escalated;           // bot couldn't answer

  if (isSystem) {
    return (
      <div style={{ textAlign: "center", margin: "10px 0" }}>
        <span style={{
          fontSize: 11, color: "#888", background: "#f0f0f0",
          borderRadius: 12, padding: "4px 12px",
        }}>
          {msg.content}
        </span>
      </div>
    );
  }

  return (
    <div style={{
      display: "flex",
      justifyContent: isUser ? "flex-end" : "flex-start",
      marginBottom: compact ? 8 : 12,
      alignItems: "flex-end",
      gap: 8,
    }}>
      {/* Bot avatar */}
      {isBot && (
        <div style={{
          width: compact ? 28 : 34, height: compact ? 28 : 34,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: compact ? 14 : 16, flexShrink: 0,
        }}>
          {BOT_AVATAR}
        </div>
      )}

      <div style={{ maxWidth: compact ? "82%" : "72%" }}>
        {/* sender label */}
        {isBot && (
          <div style={{ fontSize: 10, color: "#888", marginBottom: 3, paddingLeft: 4 }}>
            {BOT_NAME}
          </div>
        )}

        {/* bubble */}
        <div style={{
          padding: compact ? "8px 12px" : "10px 14px",
          borderRadius: isUser
            ? "16px 16px 4px 16px"
            : "16px 16px 16px 4px",
          background: isUser
            ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            : isEscalated
              ? "#fff8e1"
              : "#f4f4f8",
          color: isUser ? "#fff" : "#333",
          fontSize: compact ? 12.5 : 14,
          lineHeight: 1.5,
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          border: isEscalated ? "1px solid #ffe082" : "none",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}>
          {msg.content}
        </div>

        {/* timestamp */}
        <div style={{
          fontSize: 10, color: "#bbb", marginTop: 3,
          textAlign: isUser ? "right" : "left",
          paddingLeft: isUser ? 0 : 4,
        }}>
          {formatTime(msg.ts)}
        </div>

        {/* admin-only: source (DB cache vs Claude) + API cost */}
        {isAdmin && isBot && <MessageMeta msg={msg} />}
      </div>
    </div>
  );
}

/* ── typing indicator ───────────────────────────────────────────────── */
function TypingIndicator({ compact }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 8, marginBottom: 10 }}>
      <div style={{
        width: compact ? 28 : 34, height: compact ? 28 : 34,
        borderRadius: "50%",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: compact ? 14 : 16, flexShrink: 0,
      }}>
        {BOT_AVATAR}
      </div>
      <div style={{
        padding: "10px 16px",
        borderRadius: "16px 16px 16px 4px",
        background: "#f4f4f8",
        display: "flex", gap: 5, alignItems: "center",
      }}>
        {[0, 1, 2].map(i => (
          <span key={i} style={{
            width: 7, height: 7, borderRadius: "50%",
            background: "#aaa",
            animation: `typing-dot 1.2s ${i * 0.2}s infinite`,
            display: "inline-block",
          }} />
        ))}
      </div>
    </div>
  );
}

/* ── main component ─────────────────────────────────────────────────── */
export default function ChatWindow({
  messages = [],
  onSend,
  loading = false,
  compact = false,
  onOpenFullPage,
  sessionId,
  hasUnread,
  initialDraft = "",
  isAdmin = false,
}) {
  const [draft, setDraft] = useState(initialDraft || "");
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  /* when a help bubble pre-fills a message, populate the input */
  useEffect(() => {
    if (initialDraft) {
      setDraft(initialDraft);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [initialDraft]);

  /* auto-scroll to bottom */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const handleSend = () => {
    const text = draft.trim();
    if (!text || loading) return;
    setDraft("");
    onSend?.(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      height: "100%",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    }}>
      {/* ── messages area ── */}
      <div style={{
        flex: 1, overflowY: "auto",
        padding: compact ? "12px 12px 4px" : "20px 20px 8px",
        scrollbarWidth: "thin",
        scrollbarColor: "#ddd transparent",
      }}>
        {messages.length === 0 && (
          <WelcomeMessage compact={compact} />
        )}

        {messages.map(msg => (
          <MessageBubble key={msg.id} msg={msg} compact={compact} isAdmin={isAdmin} />
        ))}

        {loading && <TypingIndicator compact={compact} />}

        <div ref={bottomRef} />
      </div>

      {/* ── disclaimer strip ── */}
      <div style={{
        padding: compact ? "5px 12px" : "6px 20px",
        background: "#fffbf0",
        borderTop: "1px solid #ffeaa7",
        fontSize: compact ? 9.5 : 11,
        color: "#8a7a50",
        lineHeight: 1.4,
      }}>
        {DISCLAIMER}
      </div>

      {/* ── input area ── */}
      <div style={{
        padding: compact ? "10px 12px" : "14px 20px",
        borderTop: "1px solid #eee",
        background: "#fff",
        display: "flex", gap: 8, alignItems: "flex-end",
      }}>
        <textarea
          ref={inputRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Ask me anything about ${appName}...`}
          disabled={loading}
          rows={1}
          style={{
            flex: 1,
            border: "1px solid #e0e0e0",
            borderRadius: 20,
            padding: "9px 14px",
            fontSize: compact ? 12.5 : 14,
            resize: "none",
            outline: "none",
            fontFamily: "inherit",
            lineHeight: 1.5,
            maxHeight: 100,
            overflowY: "auto",
            background: loading ? "#fafafa" : "#fff",
            color: "#333",
            transition: "border-color 0.2s",
          }}
          onFocus={e => e.target.style.borderColor = "#667eea"}
          onBlur={e => e.target.style.borderColor = "#e0e0e0"}
        />
        <button
          onClick={handleSend}
          disabled={!draft.trim() || loading}
          style={{
            width: compact ? 36 : 42, height: compact ? 36 : 42,
            borderRadius: "50%",
            background: (!draft.trim() || loading)
              ? "#e0e0e0"
              : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            border: "none",
            cursor: (!draft.trim() || loading) ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
            transition: "background 0.2s",
          }}
        >
          {loading
            ? <Spinner size="sm" style={{ width: 16, height: 16, color: "#999" }} />
            : <SendIcon size={compact ? 14 : 16} />
          }
        </button>
      </div>

      {/* ── "open full page" link (compact mode only) ── */}
      {compact && onOpenFullPage && (
        <div style={{
          padding: "6px 12px 10px",
          textAlign: "center",
          background: "#fff",
          borderTop: "1px solid #f0f0f0",
        }}>
          <button
            onClick={onOpenFullPage}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "#667eea", fontSize: 11, textDecoration: "underline",
            }}
          >
            Open full chat ↗
          </button>
        </div>
      )}

      <style>{`
        @keyframes typing-dot {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-5px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

/* ── welcome screen ─────────────────────────────────────────────────── */
function WelcomeMessage({ compact }) {
  const suggestions = [
    "How do I build a strategy?",
    "How does the rule engine work?",
    "What is Pyramid Hedging?",
    "How do I set up Tradier?",
  ];

  return (
    <div style={{ textAlign: "center", padding: compact ? "10px 0 16px" : "20px 0 24px" }}>
      <div style={{ fontSize: compact ? 32 : 48, marginBottom: 8 }}>🤖</div>
      <div style={{ fontWeight: 700, fontSize: compact ? 14 : 18, color: "#333", marginBottom: 4 }}>
        {BOT_NAME}
      </div>
      <div style={{ fontSize: compact ? 11.5 : 13, color: "#888", marginBottom: compact ? 12 : 20, lineHeight: 1.5 }}>
        Hi! I'm here to help you with {appName}.<br />
        Ask me about strategies, rules, or how the platform works.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {suggestions.map(s => (
          <SuggestionChip key={s} text={s} compact={compact} />
        ))}
      </div>
    </div>
  );
}

function SuggestionChip({ text, compact, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: "#f4f4f8", border: "1px solid #e8e8ef",
        borderRadius: 20, padding: compact ? "6px 12px" : "8px 16px",
        fontSize: compact ? 11 : 13, color: "#555",
        cursor: "pointer", textAlign: "center",
        transition: "background 0.15s",
      }}
      onMouseEnter={e => e.target.style.background = "#ebe8f7"}
      onMouseLeave={e => e.target.style.background = "#f4f4f8"}
    >
      {text}
    </button>
  );
}

/* ── icons ───────────────────────────────────────────────────────────── */
function SendIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M22 2L11 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/* ── helpers ─────────────────────────────────────────────────────────── */
function formatTime(ts) {
  if (!ts) return "";
  const d = typeof ts === "string" ? new Date(ts) : ts;
  if (isNaN(d)) return "";
  return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}
